'use client';

import { cn } from '@core/utils';
import useEmblaCarousel from 'embla-carousel-react';
import { useCallback, useEffect, useState } from 'react';

import { AdBadge } from '@/app/_components/ads';
import { CustomLink } from '@/app/_components/CustomLink';
import { Image } from '@/components/client';
import { BREAKPOINTS } from '@/constants';
import { AD_UNIT_IDS } from '@/constants/ad';
import { AD_MOCK_DATA, AD_MOCK_MODE, isAdHidden, useAd } from '@/hooks/useAd';
import { useMediaQuery } from '@/hooks/useMediaQuery';

interface DesktopMainBannerSectionProps {
  bannerItems: {
    href: string;
    title: string;
    imageUrl: string;
    target?: string;
    overlayTitle?: string;
    overlayCta?: string;
  }[];
  className?: string;
}

export const DesktopMainBannerSection = ({
  bannerItems,
  className,
}: DesktopMainBannerSectionProps) => {
  const isDesktop = useMediaQuery(BREAKPOINTS.DESKTOP);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isSwiperReady, setIsSwiperReady] = useState(false);

  const { containerRef, adState } = useAd(AD_UNIT_IDS.DONATION_HOME_TOP);
  const showAd = !isAdHidden(adState);

  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: bannerItems.length > 1,
    align: 'center',
  });

  //* 슬라이드 선택 시 현재 인덱스 업데이트
  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setActiveIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    setIsSwiperReady(true);
    onSelect();
    emblaApi.on('select', onSelect);

    return () => {
      emblaApi.off('select', onSelect);
    };
  }, [emblaApi, onSelect]);

  const bannerImageWidth = isDesktop
    ? BANNER_WIDTH_DESKTOP
    : BANNER_WIDTH_MOBILE;
  const swiperWidth =
    bannerImageWidth * MIN_BANNER_LENGTH + SWIPER_GAP * (MIN_BANNER_LENGTH - 1);

  //* 배너가 최소 개수보다 적을 때 화면을 채우도록 리스트 확장
  const list = (() => {
    if (bannerItems.length === MIN_BANNER_LENGTH)
      return [...bannerItems, ...bannerItems];

    if (bannerItems.length < MIN_BANNER_LENGTH) {
      let newList = [...bannerItems];
      let sortedListIndex = 0;
      for (
        let i = 0;
        i < bannerItems.length * (MIN_BANNER_LENGTH - bannerItems.length) * 2;
        i++
      ) {
        const newIndexItem = bannerItems[sortedListIndex];
        if (!newIndexItem) {
          sortedListIndex = 0;
        }

        newList.push(bannerItems[sortedListIndex]);
        sortedListIndex++;
      }

      return newList;
    }

    return bannerItems;
  })();

  //* 페이지네이션: 복제된 리스트에서의 activeIndex → 원본 기준 인덱스
  const totalUniqueItems = bannerItems.length + (showAd ? 1 : 0);
  const paginationIndex = showAd
    ? activeIndex === 0
      ? 0
      : 1 + ((activeIndex - 1) % bannerItems.length)
    : activeIndex % bannerItems.length;

  const isNotRender = bannerItems.length < 1;

  if (isNotRender) return null;

  return (
    <section
      className={cn('w-fit max-w-full', 'mobile-tablet:w-full', className)}
      key={isDesktop ? 'desktop' : 'mobile'}
    >
      <div className="relative">
        {bannerItems.length > 1 && (
          <div
            className="pointer-events-none absolute z-10 hidden mobile-tablet:block"
            style={{
              bottom: 12,
              right: `calc((100% - ${BANNER_WIDTH_MOBILE}px) / 2 + 12px)`,
            }}
          >
            <div className="h-20 w-36 whitespace-nowrap rounded-pill bg-Gray-black/20 font-600 text-Gray-white flex-center typo-caption2">
              {`${paginationIndex + 1} / ${totalUniqueItems}`}
            </div>
          </div>
        )}
        <div
          className={cn(
            'max-w-full overflow-hidden transition-opacity duration-300',
            {
              'opacity-0': !isSwiperReady,
              'opacity-100': isSwiperReady,
            },
          )}
          style={{ width: `${swiperWidth}px` }}
          ref={emblaRef}
        >
          <div className="flex">
            {showAd && (
              <div
                ref={containerRef}
                className="flex-[0_0_auto] cursor-pointer"
                style={{
                  width: bannerImageWidth,
                  minWidth: 0,
                  marginRight: `${SWIPER_GAP}px`,
                }}
              >
                <div
                  className={cn(
                    'relative aspect-[335/120] overflow-hidden rounded-large',
                    'transition-transform duration-300',
                    {
                      'mobile-tablet:scale-y-100': activeIndex === 0,
                      'mobile-tablet:scale-y-90': activeIndex !== 0,
                    },
                    adState === 'loading' && 'animate-pulse bg-Gray-100',
                  )}
                >
                  <AdBadge />
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    data-adrop-native="asset"
                    src={AD_MOCK_MODE ? AD_MOCK_DATA.asset : undefined}
                    alt=""
                    className="size-full rounded-large border-0 object-cover"
                  />
                  <p data-adrop-native="headline" className="hidden" />
                  <p data-adrop-native="body" className="hidden" />
                </div>
              </div>
            )}
            {list.map(
              (
                { href, imageUrl, title, target, overlayTitle, overlayCta },
                index,
              ) => (
                <div
                  key={`${href}-${index}`}
                  className="flex-[0_0_auto]"
                  style={{
                    width: bannerImageWidth,
                    minWidth: 0,
                    marginRight: `${SWIPER_GAP}px`,
                  }}
                >
                  <CustomLink
                    href={href}
                    target={target}
                    aria-label={`${title} 상세페이지로 이동`}
                    className="mobile-tablet:w-full"
                  >
                    <div
                      className={cn(
                        'relative h-180 overflow-hidden rounded-large',
                        'transition-transform duration-300 mobile-tablet:h-120',
                        {
                          'mobile-tablet:scale-y-100':
                            activeIndex === index + (showAd ? 1 : 0),
                          'mobile-tablet:scale-y-90':
                            activeIndex !== index + (showAd ? 1 : 0),
                        },
                      )}
                    >
                      <Image
                        src={imageUrl}
                        fill
                        sizes={`${BREAKPOINTS.MOBILE_TABLET} 335px, 502px`}
                        alt={`${title}에 관한 배너 이미지`}
                        className="rounded-large object-cover"
                        priority={index === 0}
                      />
                      {(overlayTitle || overlayCta) && (
                        <>
                          <div
                            aria-hidden
                            className="absolute inset-0 bg-gradient-to-r from-O-400 via-O-400/90 to-transparent"
                          />
                          <div className="absolute inset-0 flex flex-col justify-center gap-12 px-24 text-Gray-white mobile-tablet:gap-8 mobile-tablet:px-20">
                            {overlayTitle && (
                              <p className="font-700 typo-h5 mobile-tablet:typo-h7">
                                {overlayTitle}
                              </p>
                            )}
                            {overlayCta && (
                              <span className="inline-flex w-fit items-center gap-4 rounded-pill bg-Gray-white px-12 py-4 font-600 text-O-400 typo-body4 mobile-tablet:typo-caption1">
                                {overlayCta}
                                <span aria-hidden>→</span>
                              </span>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </CustomLink>
                </div>
              ),
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

const BANNER_WIDTH_DESKTOP = 502;
const BANNER_WIDTH_MOBILE = 335;
const MIN_BANNER_LENGTH = 5;
const SWIPER_GAP = 10;
